import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  FiSearch, FiPhone, FiClock, FiUser,
  FiChevronLeft, FiChevronRight,
} from 'react-icons/fi';
import type { CallLog, VoiceAgent } from '../voice.types';
import '../../../styles/Voice.css';
import '../../../styles/MomentumCommand.css';
import '../../../styles/AdminDashboard.css';

const PAGE_SIZE = 10;

interface CallLogsTabProps {
  calls: CallLog[];
  agents: VoiceAgent[];
  onCallClick: (call: CallLog) => void;
  loading?: boolean;
  search?: string;
  onSearchChange?: (v: string) => void;
}

const DIRECTION_CONFIG = {
  in:     { bg: 'rgba(52,211,153,0.12)',  border: 'rgba(52,211,153,0.25)',  color: 'var(--accent-green)',        label: 'Inbound' },
  out:    { bg: 'rgba(139,92,246,0.12)',  border: 'rgba(139,92,246,0.25)',  color: 'var(--accent-purple-light)', label: 'Outbound' },
  missed: { bg: 'rgba(239,68,68,0.1)',    border: 'rgba(239,68,68,0.2)',    color: 'var(--accent-red)',          label: 'Missed' },
};

const CallLogsTab: React.FC<CallLogsTabProps> = ({ calls, agents, onCallClick, loading, search: searchProp, onSearchChange }) => {
  const [searchInternal, setSearchInternal] = useState('');
  const search = searchProp ?? searchInternal;
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return calls;
    return calls.filter(c =>
      c.to.toLowerCase().includes(q) ||
      c.from.toLowerCase().includes(q) ||
      c.contactName.toLowerCase().includes(q) ||
      c.agent.toLowerCase().includes(q)
    );
  }, [calls, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paginated  = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleSearch = (v: string) => { setSearchInternal(v); onSearchChange?.(v); setPage(1); };

  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
    .reduce<(number | '...')[]>((acc, p, i, arr) => {
      if (i > 0 && p - (arr[i - 1] as number) > 1) acc.push('...');
      acc.push(p);
      return acc;
    }, []);

  return (
    <motion.div
      key="calls"
      className="voice-tab-content call-logs-tab__content"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 8 }}
      transition={{ duration: 0.18 }}
    >

      {/* ── Cards ── */}
      <div className="call-logs-tab__list-wrap">
        {loading ? (
          <div className="call-logs-tab__list">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="call-logs-tab__card" style={{ gap: 'var(--space-3)' }}>
                {/* direction icon circle */}
                <div className="skeleton skeleton-rect" style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0 }} />
                {/* caller info */}
                <div style={{ flex: '0 0 160px', display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div className="skeleton skeleton-text" style={{ width: '80%', height: 11 }} />
                  <div className="skeleton skeleton-text" style={{ width: '60%', height: 10 }} />
                </div>
                {/* direction badge */}
                <div className="skeleton skeleton-rect" style={{ width: 64, height: 20, borderRadius: 20 }} />
                {/* agent */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div className="skeleton skeleton-text" style={{ width: '50%', height: 10 }} />
                </div>
                {/* date + duration */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5, alignItems: 'flex-end' }}>
                  <div className="skeleton skeleton-text" style={{ width: 90, height: 10 }} />
                  <div className="skeleton skeleton-text" style={{ width: 40, height: 10 }} />
                </div>
                {/* view button */}
                <div className="skeleton skeleton-rect" style={{ width: 58, height: 28, borderRadius: 8, flexShrink: 0 }} />
              </div>
            ))}
          </div>
        ) : calls.length === 0 ? (
          <div className="voice-empty">
            <div className="voice-empty__icon"><FiPhone size={28} /></div>
            <div className="voice-empty__title">No call logs</div>
            <div className="voice-empty__desc">Inbound and outbound call records will appear here.</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="voice-empty">
            <div className="voice-empty__icon"><FiPhone size={28} /></div>
            <div className="voice-empty__title">No results</div>
            <div className="voice-empty__desc">No calls match your search.</div>
          </div>
        ) : (
          <div className="call-logs-tab__list">
            {paginated.map((c, i) => {
              const dir = DIRECTION_CONFIG[c.direction] ?? DIRECTION_CONFIG.out;
              const agentName = agents.find(a => a.elevenLabsAgentId === c.agentId)?.name ?? c.agent;

              return (
                <motion.div
                  key={c.id}
                  className="call-logs-tab__card"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.background = 'var(--surface-glass-hover)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'rgba(139,92,246,0.25)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = 'var(--surface-card)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-glass)';
                  }}
                >
                  {/* Direction icon */}
                  <div
                    className="call-logs-tab__direction-icon"
                    style={{ background: dir.bg, border: `1px solid ${dir.border}` }}
                  >
                    <FiPhone size={15} style={{ color: dir.color }} />
                  </div>

                  {/* Caller info */}
                  <div className="call-logs-tab__caller-info">
                    <div className="call-logs-tab__name-row">
                      <FiUser size={11} className="call-logs-tab__icon-muted" />
                      <span className="call-logs-tab__contact-name">
                        {c.contactName || c.to || '—'}
                      </span>
                    </div>
                    <div className="call-logs-tab__number-row">
                      <FiPhone size={10} className="call-logs-tab__icon-muted" />
                      <span className="call-logs-tab__number-text">
                        {c.to}
                      </span>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="call-logs-tab__divider" />

                  {/* Direction badge + agent */}
                  <div className="call-logs-tab__mid-col">
                    <span
                      className="call-logs-tab__direction-badge"
                      style={{
                        background: dir.bg, color: dir.color,
                        border: `1px solid ${dir.color}40`,
                      }}
                    >
                      {dir.label}
                    </span>
                    <span className="call-logs-tab__agent-label">
                      Agent: <span className="call-logs-tab__agent-name">{agentName || '—'}</span>
                    </span>
                  </div>

                  {/* Date + duration */}
                  <div className="call-logs-tab__date-col">
                    <span className="call-logs-tab__timestamp">
                      {c.timestamp}
                    </span>
                    <span className="call-logs-tab__duration">
                      <FiClock size={10} />{c.duration}
                    </span>
                  </div>

                  {/* View button */}
                  <button
                    className="mc-view-details-btn call-logs-tab__view-btn"
                    onClick={() => onCallClick(c)}
                  >
                    View
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="admin-pagination">
          <span className="admin-pagination__info">
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
          </span>
          <div className="admin-pagination__controls">
            <button className="admin-pagination__btn" disabled={safePage === 1} onClick={() => setPage(p => p - 1)}>
              <FiChevronLeft size={13} />
            </button>
            {pageNumbers.map((p, i) =>
              p === '...' ? (
                <span key={`dots-${i}`} className="admin-pagination__ellipsis">…</span>
              ) : (
                <button
                  key={p}
                  className={`admin-pagination__btn${p === safePage ? ' admin-pagination__btn--active' : ''}`}
                  onClick={() => setPage(p as number)}
                >
                  {p}
                </button>
              )
            )}
            <button className="admin-pagination__btn" disabled={safePage === totalPages} onClick={() => setPage(p => p + 1)}>
              <FiChevronRight size={13} />
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default CallLogsTab;
